import {announce} from '@github-ui/aria-live'
import {getNumberFieldValidationMessage, getTextFieldValidationMessage} from '@github-ui/memex-field-validators'
import {useDebounce} from '@github-ui/use-debounce'
import {FormControl, TextInput} from '@primer/react'
import {useCallback, useState} from 'react'

import {apiUpdateColumn} from '../../../api/columns/api-update-column'
import {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import {CommitState} from '../../../hooks/use-autosave'
import type {ColumnModel} from '../../../models/column-model'
import {Resources} from '../../../strings'
import styles from './default-value-settings.module.css'

type DefaultValueSettingsProps = {
  column: ColumnModel
  disabled?: boolean
}

export const DefaultValueSettings: React.FC<DefaultValueSettingsProps> = ({column, disabled = false}) => {
  // Cast settings to include defaultValue using type assertion
  const columnSettings = column.settings as {width: number; defaultValue?: string | number}
  const [defaultValue, setDefaultValue] = useState<string | number | undefined>(columnSettings?.defaultValue)
  const [numberInputValue, setNumberInputValue] = useState<string>(
    typeof columnSettings?.defaultValue === 'number' ? String(columnSettings.defaultValue) : '',
  )
  const [commitState, setCommitState] = useState<CommitState>(CommitState.None)
  const [validationMessage, setValidationMessage] = useState<string | undefined>(undefined)

  const saveValue = useCallback(
    async (valueToSave: string | number | undefined) => {
      try {
        await apiUpdateColumn({
          memexProjectColumnId: column.id,
          settings: {
            defaultValue: valueToSave ?? null,
          },
        })
        setCommitState(CommitState.Successful)
        announce(Resources.changesSaved)
        setTimeout(() => setCommitState(CommitState.None), 3000)
      } catch {
        setCommitState(CommitState.Failed)
        announce(Resources.genericErrorMessage)
        setTimeout(() => setCommitState(CommitState.None), 3000)
      }
    },
    [column.id],
  )

  const debouncedSaveValue = useDebounce(saveValue, 500)

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      const valueToSave = value || undefined
      setDefaultValue(valueToSave)
      const error = value ? getTextFieldValidationMessage(value) : undefined
      setValidationMessage(error)
      if (!error) {
        debouncedSaveValue(valueToSave)
      }
    },
    [debouncedSaveValue],
  )

  const handleNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      if (value === '') {
        setDefaultValue(undefined)
        setNumberInputValue('')
        setValidationMessage(undefined)
        debouncedSaveValue(undefined)
      } else {
        setNumberInputValue(value)
        const error = getNumberFieldValidationMessage(value)
        setValidationMessage(error)
        if (!error) {
          const num = parseFloat(value)
          setDefaultValue(num)
          debouncedSaveValue(num)
        }
      }
    },
    [debouncedSaveValue],
  )

  // Only text and number fields support default values
  const isTextField = column.dataType === MemexColumnDataType.Text
  const isNumberField = column.dataType === MemexColumnDataType.Number

  if ((!isTextField && !isNumberField) || disabled) {
    return null
  }

  return (
    <FormControl className={styles.DefaultValueFormControl}>
      <FormControl.Label>Default value</FormControl.Label>
      <FormControl.Caption>
        New items added to the project will automatically have this value set for this field.
      </FormControl.Caption>
      {isTextField ? (
        <TextInput
          value={typeof defaultValue === 'string' ? defaultValue : ''}
          onChange={handleTextChange}
          placeholder="Enter default text"
        />
      ) : (
        <TextInput
          type="number"
          value={numberInputValue}
          onChange={handleNumberChange}
          placeholder="Enter default number"
        />
      )}
      {validationMessage && <FormControl.Validation variant="error">{validationMessage}</FormControl.Validation>}
      {!validationMessage && commitState === CommitState.Successful && (
        <FormControl.Validation variant="success">{Resources.changesSaved}</FormControl.Validation>
      )}
      {!validationMessage && commitState === CommitState.Failed && (
        <FormControl.Validation variant="error">{Resources.genericErrorMessage}</FormControl.Validation>
      )}
    </FormControl>
  )
}
